import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Volume2, Pause, Play, Square, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Collect the page's readable blocks (headings + paragraphs) as DOM nodes so each
// can be highlighted while its audio plays. Skips nav, footer, buttons and tables.
const collectNodes = () => {
  const root = document.querySelector('[data-readable-root]');
  if (!root) return [];
  const els = root.querySelectorAll('h1, h2, h3, h4, p, [data-readable]');
  const out = [];
  const seen = new Set();
  els.forEach((el) => {
    const t = (el.innerText || '').replace(/\s+/g, ' ').trim();
    if (t.length > 1 && !seen.has(t)) {
      seen.add(t);
      out.push({ el, text: t });
    }
  });
  return out;
};

const ReadAloud = ({ dark = true, voice = 'nova', accent = '#3b82f6' }) => {
  const [status, setStatus] = useState('idle'); // idle | loading | playing | paused
  const [progress, setProgress] = useState({ i: 0, total: 0 });

  const nodesRef = useRef([]);
  const idxRef = useRef(0);
  const audioRef = useRef(null);
  const urlCacheRef = useRef(new Map());
  const activeElRef = useRef(null);
  const cancelledRef = useRef(false);

  const clearHighlight = useCallback(() => {
    if (activeElRef.current) {
      activeElRef.current.classList.remove('reading-active');
      activeElRef.current = null;
    }
  }, []);

  const highlight = useCallback((el) => {
    clearHighlight();
    el.classList.add('reading-active');
    activeElRef.current = el;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (e) {
      /* noop */
    }
  }, [clearHighlight]);

  const fetchAudioUrl = useCallback(async (text) => {
    const cache = urlCacheRef.current;
    if (cache.has(text)) return cache.get(text);
    const res = await fetch(`${API}/api/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, voice }),
    });
    if (!res.ok) throw new Error('tts failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    cache.set(text, url);
    return url;
  }, [voice]);

  const stop = useCallback(() => {
    cancelledRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onended = null;
      audioRef.current.src = '';
    }
    clearHighlight();
    idxRef.current = 0;
    setProgress({ i: 0, total: 0 });
    setStatus('idle');
  }, [clearHighlight]);

  const playIndex = useCallback(async (i) => {
    const nodes = nodesRef.current;
    if (cancelledRef.current) return;
    if (i >= nodes.length) {
      clearHighlight();
      idxRef.current = 0;
      setStatus('idle');
      return;
    }
    idxRef.current = i;
    setProgress({ i: i + 1, total: nodes.length });
    highlight(nodes[i].el);
    if (!urlCacheRef.current.has(nodes[i].text)) setStatus('loading');
    let url;
    try {
      url = await fetchAudioUrl(nodes[i].text);
    } catch (e) {
      if (!cancelledRef.current) playIndex(i + 1); // skip a failed chunk
      return;
    }
    if (cancelledRef.current) return;
    const audio = audioRef.current;
    audio.src = url;
    audio.onended = () => playIndex(i + 1);
    try {
      await audio.play();
      setStatus('playing');
    } catch (e) {
      /* play interrupted */
    }
    // Prefetch the next couple of blocks for a gapless transition.
    [1, 2].forEach((d) => {
      const n = nodes[i + d];
      if (n) fetchAudioUrl(n.text).catch(() => {});
    });
  }, [clearHighlight, fetchAudioUrl, highlight]);

  const start = useCallback(() => {
    const nodes = collectNodes();
    if (!nodes.length) return;
    cancelledRef.current = false;
    nodesRef.current = nodes;
    if (!audioRef.current) audioRef.current = new Audio();
    idxRef.current = 0;
    setStatus('loading');
    playIndex(0);
  }, [playIndex]);

  const togglePause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (status === 'playing') {
      audio.pause();
      setStatus('paused');
    } else if (status === 'paused') {
      audio.play().then(() => setStatus('playing')).catch(() => {});
    }
  }, [status]);

  useEffect(() => () => {
    cancelledRef.current = true;
    if (audioRef.current) audioRef.current.pause();
    if (activeElRef.current) activeElRef.current.classList.remove('reading-active');
    urlCacheRef.current.forEach((url) => URL.revokeObjectURL(url));
    urlCacheRef.current.clear();
  }, []);

  const tint = (a) => {
    const m = accent.replace('#', '');
    const r = parseInt(m.substring(0, 2), 16), g = parseInt(m.substring(2, 4), 16), b = parseInt(m.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  };
  const pillStyle = { borderColor: tint(0.4), backgroundColor: dark ? tint(0.12) : tint(0.08), color: accent };
  const pillCls = 'transition-all duration-200 hover:brightness-110';

  if (status === 'idle') {
    return (
      <button
        type="button"
        onClick={start}
        data-testid="read-aloud-toggle"
        aria-label="Read this page aloud"
        className={`group inline-flex items-center gap-2.5 rounded-full border px-5 py-2.5 text-sm font-semibold ${pillCls}`}
        style={pillStyle}
      >
        <Volume2 className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" strokeWidth={2.2} />
        <span>
          Too busy to read?{' '}
          <span className="font-extrabold underline decoration-2 underline-offset-4" style={{ textDecorationColor: accent }}>Read aloud</span>
        </span>
      </button>
    );
  }

  return (
    <div data-testid="read-aloud-controls" className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={togglePause}
        disabled={status === 'loading'}
        data-testid="read-aloud-playpause"
        aria-label={status === 'playing' ? 'Pause reading' : 'Resume reading'}
        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold disabled:opacity-60 ${pillCls}`}
        style={pillStyle}
      >
        {status === 'loading' ? (
          <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.2} /> Preparing…</>
        ) : status === 'playing' ? (
          <><Pause className="h-4 w-4" strokeWidth={2.4} /> Pause</>
        ) : (
          <><Play className="h-4 w-4" strokeWidth={2.4} /> Resume</>
        )}
      </button>
      <button
        type="button"
        onClick={stop}
        data-testid="read-aloud-stop"
        aria-label="Stop reading"
        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold ${pillCls}`}
        style={pillStyle}
      >
        <Square className="h-3.5 w-3.5" strokeWidth={2.6} /> Stop
      </button>
      {progress.total > 0 && (
        <span className={`font-mono text-xs tabular-nums ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
          {progress.i}/{progress.total}
        </span>
      )}
    </div>
  );
};

export default ReadAloud;
